import React from "react";
import { Button } from "@core/components/ui/button";
import { Link, useRouteError, isRouteErrorResponse, useNavigate } from "@remix-run/react";
import { Surface } from "@core/components/ui/surface";
import { Dialog, DialogContent, DialogHeader, DialogDescription, DialogTitle } from "@core/components/ui/dialog";

interface DefaultErrorBoundaryProps {
  link?: React.ReactNode;
  asModal?: boolean;
}

/* eslint-disable react/no-unescaped-entities */
export function DefaultErrorBoundary({ asModal, ...props}: DefaultErrorBoundaryProps) {
  const error = useRouteError();
  const navigate = useNavigate();
  return (
    <>
      {asModal ? (
        <Dialog open={true} onOpenChange={(open: boolean) => (open ? () => {} : navigate(-1))}>
          <DialogHeader>
            <DialogTitle>Error</DialogTitle>
            <DialogDescription className="sr-only">An error occurred.</DialogDescription>
          </DialogHeader>
          <DialogContent>
            <DefaultErrorBoundary />
          </DialogContent>
        </Dialog>
      ) : (
        <ErrorModeContent error={error} {...props} />
      )}
    </>
  );
}
function ErrorModeContent({ error, link }: DefaultErrorBoundaryProps & { error: unknown }) {
  return (
    <div className="container select-none">
      <div className="grid grid-cols-1">
        <div>
          <h1 className="text-center" style={{ fontSize: "7rem" }}>
            {isRouteErrorResponse(error) ? error.status : "Error"}
          </h1>
          <h2 className="text-center text-4xl mt-4">
            {isRouteErrorResponse(error) ? error.statusText : "Unknown Error"}
          </h2>
          <div className="mx-auto text-center text-gray-500 text-xl w-1/2 mt-4">
            <h1>Something went wrong</h1>
            <p>We're looking into it. Please try again later.</p>
          </div>
          <div className="flex justify-center mt-4">
            <Link className="link" to="/contact">
              Contact Us
            </Link>
          </div>
          <div className="flex justify-center mt-4">
            {link ? link : (
              <Button variant="primary" asChild={true}>
                <Link to="/" className="flex flex-row align-items-center">
                  <span className="inline-block">
                    Home
                  </span>
                </Link>
              </Button>
            )}
          </div>
          <Surface className="max-w-96 text-center m-auto mt-4">
            <>
              {isRouteErrorResponse(error) ? (
                <>
                  <p>{error.data}</p>
                </>
              ) : error instanceof Error ? (
                <p>{error.message}</p>
              ) : (
                <p>Unknown Error</p>
              )}
            </>
          </Surface>
        </div>
      </div>
    </div>
  );
}
